import React, { useState, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faClose } from "@duocards/pro-regular-svg-icons";
import { Recorder } from "tools/recorder";
import { useEvent, useMount } from "react-use";
import { dispatchEvent, EVENT, snackbar } from "tools/events";
import { useGetState, useSetState } from "components/ReduxProvider";
import { useUnmount } from "react-use";
import { useTranslation } from "react-i18next";
import { Popper, PopperProps } from "@mui/material";
import { silenceTts } from "queries/tts/ttsTools";

const NUMBER_OF_AUDIOBARS = 64;
const MIN_RECORDING_TIME_MS = 1000; // Minimum 1 second recording
// Adaptive silence detection tuning (100ms loudness window from Recorder)
const THRESHOLD_MULTIPLIER = 2.5; // active if > baseline * multiplier
const MIN_THRESHOLD_FLOOR = 0.08; // floor for scaled loudness (RMS * 10)
const MIN_ACTIVE_WINDOWS = 4; // require ~400ms above threshold
const MIN_CONSECUTIVE_ACTIVE_WINDOWS = 2; // avoid isolated clicks

// percentile to compute baseline from samples
const percentile = (arr: number[], p: number) => {
  if (!arr.length) return 0;
  const s = [...arr].sort((a, b) => a - b);
  const idx = Math.min(s.length - 1, Math.max(0, Math.floor(p * (s.length - 1))));
  return s[idx];
};

const hasMeaningfulAudio = (samples: number[]): boolean => {
  if (!samples.length) return false;

  // Ignore artificial zeros emitted before the audio pipeline warms up
  const nonZero = samples.filter((s) => s > 0);
  if (!nonZero.length) return false; // no actual readings

  // baseline from low-percentile of non-zero samples
  const baseline = percentile(nonZero, 0.2);
  const threshold = Math.max(baseline * THRESHOLD_MULTIPLIER, MIN_THRESHOLD_FLOOR);

  let activeTotal = 0;
  let consecutive = 0;
  let hasConsecutive = false;
  let maxSample = 0;

  for (const v of nonZero) {
    if (v > maxSample) maxSample = v;
    if (v >= threshold) {
      activeTotal += 1;
      consecutive += 1;
      if (consecutive >= MIN_CONSECUTIVE_ACTIVE_WINDOWS) hasConsecutive = true;
    } else {
      consecutive = 0;
    }
  }

  // for debugging
  // alert(
  //   `Samples: ${samples.length} (${nonZero.length} non-zero) | ` +
  //     `Baseline: ${baseline.toFixed(2)} | Threshold: ${threshold.toFixed(2)} | ` +
  //     `Active: ${activeTotal}/${MIN_ACTIVE_WINDOWS} | ` +
  //     `Consecutive: ${hasConsecutive} (min ${MIN_CONSECUTIVE_ACTIVE_WINDOWS}) | ` +
  //     `Max: ${maxSample.toFixed(2)}`
  // );

  if (activeTotal >= MIN_ACTIVE_WINDOWS && hasConsecutive) return true;
  if (maxSample >= Math.max(0.3, threshold * 1.5) && activeTotal >= 2) return true;
  return false;
};

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  padding: 15px 20px;
  gap: 15px;
  width: calc(100%);
  max-width: 400px;
  height: 60px;
  margin-bottom: 10px;
  border-radius: 30px;
  background-color: ${({ theme }) => theme.duo.palette.blueMain};
`;

const IconButton = styled.button<{ color: "primary" | "secondary" }>`
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  background-color: ${({ theme, color }) => (color === "primary" ? theme.duo.color.white : theme.duo.palette.blueMain)};
  color: ${({ theme, color }) => (color === "primary" ? theme.duo.palette.blueMain : theme.duo.color.white)};
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${({ theme, color }) => theme.duo.color.white};

  > svg {
    width: 16px;
    height: 16px;
  }
`;

const LoudnessGraph = styled.div`
  display: flex;
  height: 100%;
  overflow: hidden;
  justify-content: flex-start;
  align-items: center;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to right,
      ${({ theme }) => theme.duo.palette.blueMain} 0%,
      transparent 10%,
      transparent 90%,
      ${({ theme }) => theme.duo.palette.blueMain} 100%
    );
    pointer-events: none;
  }
`;

const LoudnessBar = styled.div`
  width: 4px;
  margin-right: 1px;
  background-color: ${({ theme }) => theme.duo.color.white};
  border-radius: 4px;
`;

const barWave = keyframes`
  0% { transform: scaleY(0.25); }
  50% { transform: scaleY(1); }
  100% { transform: scaleY(0.25); }
`;

const InitializingBar = styled(LoudnessBar)<{ $delay: number }>`
  height: 60%;
  transform-origin: center center;
  will-change: transform;
  animation: ${barWave} 1200ms ease-in-out infinite;
  animation-delay: ${({ $delay }) => `-${$delay}ms`};
  opacity: 0.95;
  background-color: ${({ theme }) => theme.duo.color.lightBlue};
`;

const showArrowAnim = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Arrow = styled.span`
  position: absolute;
  width: 10px;
  height: 10px;
  &::before {
    content: "";
    margin: auto;
    display: block;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 10px 10px 0px 10px;
    border-color: ${({ theme }) => theme.duo.palette.blueMain} transparent transparent transparent;
    opacity: 0;
    animation: ${showArrowAnim} 0.3s forwards;
  }
`;

const PopperWrapper = styled(Popper)`
  z-index: 1;
  transition: none !important;
  &[data-popper-placement*="top"] ${Arrow} {
    bottom: 0;
    left: 0;
    width: 10px;
    height: 10px;
    margin-left: -5px;
  }
`;

export type RecordingResultDetail = {
  audioBlob: Blob;
};

export type RecordingOptions = {
  stopAfterMs?: number;
  sourceElementRef?: React.RefObject<HTMLElement>;
};

export type RecordingStartDetail = RecordingOptions;

const initialLoudnessGraph = Array(NUMBER_OF_AUDIOBARS).fill(-1);

const AudioInput: React.FC = () => {
  const recorderRef = useRef<Recorder | null>(null);
  const [loudnessGraph, setLoudnessGraph] = useState<number[]>(initialLoudnessGraph);
  const recording = useGetState("isAudioRecording");
  const setRecording = useSetState("isAudioRecording");
  const [initializing, setInitializing] = useState(false);
  const { t } = useTranslation();
  const [arrowEl, setArrowEl] = React.useState<HTMLSpanElement | null>(null);
  const recordingTimeoutRef = useRef<any>(null);
  const recordingStartTimeRef = useRef<number | null>(null);
  // Collect loudness samples for adaptive analysis
  const loudnessSamplesRef = useRef<number[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<PopperProps["anchorEl"]>(null);
  const [sourceElement, setSourceElement] = useState<HTMLElement | null>(null);

  const handleStopRecording = () => {
    setRecording(false);
    recorderRef.current?.stop();
  };

  const handleStartRecording = async (event: CustomEvent<RecordingStartDetail>) => {
    const { stopAfterMs, sourceElementRef } = event.detail;
    if (recording || initializing) return;

    const sourceElement = sourceElementRef?.current;
    setSourceElement(sourceElement || null);
    const rect = sourceElement?.getBoundingClientRect(); // get rect here to avoid rerendering when sourceEl changed
    setAnchorEl(
      rect
        ? {
            getBoundingClientRect: () => {
              return new DOMRect(rect.left, rect.top, rect.width, rect.height);
            }
          }
        : null
    );

    silenceTts(); // also fixing mic initialization delay
    setInitializing(true);
    setRecording(true);
    setLoudnessGraph(initialLoudnessGraph);

    loudnessSamplesRef.current = [];

    recorderRef.current = new Recorder();

    recorderRef.current.onError((err: Error) => {
      snackbar(t(err.message), { severity: "error" });
      dispatchEvent(EVENT.recordingStopped);
    });

    recorderRef.current.onStop((audioBlob: Blob) => {
      const recordingDuration = recordingStartTimeRef.current ? Date.now() - recordingStartTimeRef.current : 0;

      // Only dispatch recordingResult if duration is sufficient, audioBlob has meaningful content,
      // and we detected non-silent audio during the session.
      // 2kb should be in our mp3 settings more than 0.1s, when using cordova audioinput it is about 418b per frame
      const hasActiveAudio = hasMeaningfulAudio(loudnessSamplesRef.current);
      if (recordingDuration >= MIN_RECORDING_TIME_MS && audioBlob.size > 2048 && hasActiveAudio) {
        dispatchEvent(EVENT.recordingResult, { audioBlob });
      }

      dispatchEvent(EVENT.recordingStopped);
    });

    recorderRef.current.onLoudness((loudness: number) => {
      // Track for adaptive silence detection (100ms windows)
      loudnessSamplesRef.current.push(loudness);
      setLoudnessGraph((prev) => {
        return [...prev.slice(Math.max(0, prev.length - NUMBER_OF_AUDIOBARS + 1)), loudness];
      });
    });

    // wait 3 frames to make sure initialized UI is painted
    const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await nextFrame();
    await nextFrame();
    await nextFrame();

    // It might have been cancelled while waiting for frames
    if (!recorderRef.current) {
      setInitializing(false);
      setRecording(false);
      return;
    }

    const started = await recorderRef.current.start();
    if (!started) {
      setInitializing(false);
      setRecording(false);
      return;
    }

    setInitializing(false);

    recordingStartTimeRef.current = Date.now();
    // limit recording time to 30 seconds
    recordingTimeoutRef.current = setTimeout(() => {
      dispatchEvent(EVENT.stopRecording);
    }, stopAfterMs || 30000);
  };

  const handleCancelRecording = () => {
    recorderRef.current?.cancel();
    recorderRef.current = null;
    recordingStartTimeRef.current = null;
    setRecording(false);
    setInitializing(false);
    dispatchEvent(EVENT.recordingStopped);
  };

  useEvent(EVENT.startRecording, handleStartRecording);
  useEvent(EVENT.stopRecording, handleStopRecording);
  useEvent(EVENT.recordingStopped, () => {
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
    setInitializing(false);
    setRecording(false);
  });

  const popperOpen = !!(recording || initializing) && !!anchorEl;

  useMount(() => {
    setRecording(false);
  });

  useUnmount(() => {
    setRecording(false);
    recorderRef.current?.uninitialize();
    recorderRef.current = null;
    recordingStartTimeRef.current = null;
  });

  const showArrow = sourceElement?.dataset?.recordingArrow !== "hide";

  return (
    <PopperWrapper
      open={popperOpen}
      placement="top"
      anchorEl={anchorEl}
      style={{ zIndex: 10000 }}
      keepMounted={true}
      modifiers={[
        {
          name: "arrow",
          enabled: !!arrowEl && showArrow,
          options: {
            element: arrowEl
          }
        },
        {
          name: "preventOverflow",
          enabled: true,
          options: {
            rootBoundary: "document"
          }
        }
      ]}
    >
      <Wrapper>
        {showArrow && <Arrow ref={setArrowEl} />}
        <IconButton color="secondary" onClick={handleCancelRecording}>
          <FontAwesomeIcon icon={faClose} />
        </IconButton>
        <LoudnessGraph>
          {initializing
            ? Array.from({ length: NUMBER_OF_AUDIOBARS }).map((_, i) => <InitializingBar key={i} $delay={i * 30} />)
            : loudnessGraph.map((value, index) => (
                <LoudnessBar
                  key={`${value}-${index}`}
                  style={{
                    height: `${8 + (value <= 0 ? 0 : value) * 100}%`,
                    color: value === -1 ? "transparent" : "auto"
                  }}
                />
              ))}
        </LoudnessGraph>
        <IconButton color="primary" onClick={handleStopRecording}>
          <FontAwesomeIcon icon={faCheck} size="xs" />
        </IconButton>
      </Wrapper>
    </PopperWrapper>
  );
};

export default AudioInput;
